import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import ConfirmModal from "@/components/strategy/ConfirmModal";
import StepIndicator from "@/components/strategy/StepIndicator";
import Step1Niche from "@/components/strategy/Step1Niche";
import Step2Duration from "@/components/strategy/Step2Duration";
import Step3Audience from "@/components/strategy/Step3Audience";
import Step4Frequency from "@/components/strategy/Step4Frequency";
import Step5Offer from "@/components/strategy/Step5Offer";
import { useSession } from "@/contexts/SessionContext";
import { useStrategyState } from "@/hooks/useStrategyState";
import { api } from "@/lib/api";

/**
 * Orchestrates the 5-step wizard. The confirmation modal, loading state and
 * roadmap render are owned here so parent (`StrategyPage`) can focus on
 * route gating and shell layout.
 *
 * Parent passes `onGenerated(roadmap)` so it can swap into the roadmap view.
 */
export default function StrategyWizard({ onGenerated, onStartGenerating }) {
    const { user, balance, setBalanceFast } = useSession();
    const state = useStrategyState(user?.user_id || null);
    const {
        step,
        form,
        updateField,
        toggleArrayField,
        stepValid,
        allValid,
        nextStep,
        prevStep,
        resolvedNiche,
        serverPayload,
        clearDraft,
        hydrated,
    } = state;

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const highestCompleted = [1, 2, 3, 4, 5].reduce(
        (acc, n) => (stepValid[n] ? n : acc),
        0
    );

    const handleNext = () => {
        if (step < 5) {
            nextStep();
            return;
        }
        if (allValid) {
            setConfirmOpen(true);
        } else {
            toast.error("Fill in all steps before generating.");
        }
    };

    const handleGenerate = async () => {
        setConfirmOpen(false);
        setSubmitting(true);
        onStartGenerating?.();
        try {
            const { data } = await api.post("/strategy/generate", serverPayload);
            if (typeof data.balance_after === "number") {
                setBalanceFast(data.balance_after);
            }
            // Attach the submitted request so the roadmap can render tone chips etc.
            const roadmap = { ...data, request: serverPayload };
            clearDraft();
            onGenerated?.(roadmap);
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 402) {
                toast.error("Not enough NXT", {
                    description:
                        typeof detail === "object"
                            ? `${detail.required} required, ${detail.balance} available.`
                            : "Top up or upgrade to continue.",
                });
            } else if (status === 403) {
                // Centralized interceptor opens SubscriptionDrawer — no toast.
            } else {
                toast.error("Generation failed", {
                    description:
                        typeof detail === "string"
                            ? detail
                            : detail?.message || err?.message || "Try again shortly.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!hydrated) {
        return (
            <div className="flex min-h-[260px] items-center justify-center text-[#8A93A6]">
                <span className="font-mono text-[10px] uppercase tracking-[0.32em]">
                    hydrating draft…
                </span>
            </div>
        );
    }

    return (
        <div
            className="flex flex-col gap-6"
            data-testid="strategy-wizard"
        >
            <StepIndicator current={step} completedUpTo={highestCompleted} />

            <div
                className="relative overflow-hidden rounded-2xl border border-[#1F2A40] bg-[#111726] p-6 sm:p-8"
                data-testid="strategy-wizard-card"
            >
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -16 }}
                        transition={{ duration: 0.25 }}
                        data-testid={`strategy-wizard-step-${step}`}
                    >
                        {step === 1 && (
                            <Step1Niche form={form} updateField={updateField} />
                        )}
                        {step === 2 && (
                            <Step2Duration
                                form={form}
                                updateField={updateField}
                                toggleArrayField={toggleArrayField}
                            />
                        )}
                        {step === 3 && (
                            <Step3Audience
                                form={form}
                                updateField={updateField}
                                toggleArrayField={toggleArrayField}
                            />
                        )}
                        {step === 4 && (
                            <Step4Frequency
                                form={form}
                                updateField={updateField}
                                toggleArrayField={toggleArrayField}
                            />
                        )}
                        {step === 5 && (
                            <Step5Offer form={form} updateField={updateField} />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Nav row */}
            <div className="flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={prevStep}
                    disabled={step <= 1 || submitting}
                    data-testid="strategy-wizard-back"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#1F2A40] bg-[#111726] px-4 py-2 text-xs font-medium uppercase tracking-[0.14em] text-[#E8ECF5] transition-all hover:border-[#00F5D4]/50 hover:text-[#00F5D4] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Back
                </button>
                <span
                    className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#8A93A6]"
                    data-testid="strategy-wizard-progress"
                >
                    step {step} / 5
                </span>
                <button
                    type="button"
                    onClick={handleNext}
                    disabled={!stepValid[step] || submitting}
                    data-testid="strategy-wizard-next"
                    className={[
                        "inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-all",
                        !stepValid[step] || submitting
                            ? "cursor-not-allowed bg-[#1F2A40] opacity-60"
                            : "bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] hover:shadow-[0_0_22px_rgba(0,245,212,0.45)]",
                    ].join(" ")}
                >
                    {step === 5 ? (
                        <>
                            <Sparkles className="h-3.5 w-3.5" />
                            Review & Generate
                        </>
                    ) : (
                        <>
                            Next
                            <ChevronRight className="h-3.5 w-3.5" />
                        </>
                    )}
                </button>
            </div>

            <ConfirmModal
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleGenerate}
                form={form}
                resolvedNiche={resolvedNiche}
                balance={balance}
            />
        </div>
    );
}
